/**
 * Guest → register hand-off for "Сохранить поиск".
 *
 * A signed-out user who picks a category/filter and clicks Save has their
 * intended search stashed in localStorage, then gets sent to /register. After
 * they authenticate, `PendingSavedSearchSync` reads this stash, creates the
 * saved search for real, and clears it (see pending-saved-search-sync.tsx).
 *
 * All access is guarded for SSR / private-mode (no window, or storage throwing).
 */

export const PENDING_SAVED_SEARCH_KEY = 'uztender_pending_saved_search';

/** Filter snapshot to replay as a saved search once the user is authed. Field
 *  names mirror {@link CreateSavedSearchInput} so it maps straight through. */
export interface PendingSavedSearch {
  name?: string;
  keyword?: string;
  regionCode?: string;
  categorySlug?: string;
  ikpuCode?: string;
  /** Slug of the niche (Spec #3 classification layer) the guest picked, e.g.
   *  from the niche search-as-you-type. Mirrors `ikpuCode` alongside it. */
  nicheSlug?: string;
  /** Fixed catalog layer the guest was browsing (e.g. 'ifi' on /ifi) when
   *  they clicked Save. /ifi is a public, indexable page a signed-out
   *  visitor can browse, so dropping this here would leave the "wrong
   *  layer" defect alive for exactly the users most likely to hit it. */
  scope?: string;
  /** Set when the guest was browsing `/supply` (`scope === 'supply'`) — see
   *  `SaveSearchButton`. A search born on that section must opt IN to
   *  supply matching or it silently matches nothing once created, and the
   *  guest hand-off would otherwise lose that the moment they registered. */
  includeSupply?: boolean;
  /**
   * A guest saving the /results card — the reader's `results` search, not a
   * lots search. When set, the replay calls `PUT /me/results-search` with this
   * and ignores every lots field above.
   */
  /** `name` is a leftover of older stashes — ignored; the name is fixed. */
  results?: { criteria: object; name?: string };
}

export function stashPendingSavedSearch(input: PendingSavedSearch): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(PENDING_SAVED_SEARCH_KEY, JSON.stringify(input));
  } catch {
    // Storage unavailable (private mode / quota) — silently skip; the guest just
    // lands on /register without the auto-save, which is an acceptable fallback.
  }
}

export function readPendingSavedSearch(): PendingSavedSearch | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PENDING_SAVED_SEARCH_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingSavedSearch;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingSavedSearch(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(PENDING_SAVED_SEARCH_KEY);
  } catch {
    // no-op
  }
}
